import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { X, Plus, Trash2, ChevronLeft, ChevronRight, ChevronDown, Loader2, Sparkles, Beaker, Copy, Upload, Calendar, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { createEval, updateEval, getAgentNodes, getAgentParameters, getAgentVersions, listModels } from '@/services/evalApi';
import { evalFunctionApi, EvalFunction, EvalFunctionConfigSchema, EvalFunctionConfigProperty } from '@/services/evalFunctionApi';
import { getAuthHeaders } from '@/services/authConfig';
import { ObjectivesEditor, Objective } from './ObjectivesEditor';
import { EvaluationSetup, EvaluationConfig, configToObjectives, objectivesToConfig } from './EvaluationSetup';

// Types
interface Agent {
  id: string;  // Content store UUID for reliable API calls
  path: string;  // Human-readable folder_path/name for display
  name: string;
  description?: string;
}

interface Model {
  value: string;
  label: string;
  provider: string;
}

// ScenarioRow is just a simple record - columns derived from agentVariables
type ScenarioRow = Record<string, string>;

// Variable mode: 'default' means same value for all scenarios, 'scenario' means per-scenario values
type VariableMode = 'default' | 'scenario';

interface Variant {
  name: string;
  model: string;
  temperature: number;
  agentVersion?: number | null;  // null = latest
  graphSettings?: {
    parallelExecution: boolean;
    maxParallelTasks: number;
  };
}

interface ContentVersion {
  version_number: number;
  created_at: string;
  commit_message?: string;
  is_draft: boolean;
  content_hash?: string;
}

interface AgentVariable {
  name: string;
  type: string;
  description?: string;
  required?: boolean;
  default?: string;
}

interface AgentHITLInfo {
  enabled: boolean;
  decision_points: string[];
  has_hitl_tool: boolean;
  has_hitl_nodes: boolean;
}

// Objective type is imported from ObjectivesEditor

interface OptimizableField {
  fieldPath: string;
  fieldType: string;  // 'text' | 'numeric' | 'enum' | 'list'
  mutationStrategy: string;  // 'llm_rewrite' | 'interpolate' | 'perturb' | 'add_remove'
  enabled: boolean;
}

interface OptimizationSettings {
  enabled: boolean;
  maxIterations: number;
  maxEvalRuns: number;
  maxDurationHours: number;
  maxCostDollars?: number;
  trialsPerIteration: number;
  checkpointInterval: number;
  autoApproveThreshold?: number;
  checkpointTimeoutHours: number;
  initialMode: 'sequential' | 'parallel';
  beamWidth: number;
  plateauWindow: number;
  plateauThreshold: number;
  optimizerModel: string;
  // User guidance for the optimizer
  goalDescription: string;
  directives: string[];
}

interface HITLMockResponse {
  decisionPointId: string;
  response: Record<string, unknown>;
}

interface HITLConfig {
  strategy: 'bypass' | 'mock' | 'llm_responder';
  mockResponses: HITLMockResponse[];
  bypassResponse: Record<string, unknown>;
  llmModel: string;
  llmPersona: string;
  llmTemperature: number;
}

interface EvalWizardProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  editMode?: boolean;
  evalId?: string;
  initialData?: Record<string, unknown>;
  /** When true, shows "Start Optimization" UI and calls start API on submit */
  startOptimizationMode?: boolean;
}

// Constants
const WIZARD_STEPS_STANDARD = [
  { id: 'basics', label: 'Basics', description: 'Name and mode' },
  { id: 'agent', label: 'Agent', description: 'Agent and scenarios' },
  { id: 'variants', label: 'Variants', description: 'A/B test variants' },
  { id: 'judge', label: 'Judge', description: 'Evaluation criteria' },
  { id: 'advanced', label: 'Advanced', description: 'Settings' },
];

const WIZARD_STEPS_OPTIMIZATION = [
  { id: 'basics', label: 'Basics', description: 'Name and mode' },
  { id: 'agent', label: 'Agent', description: 'Agent and scenarios' },
  { id: 'evaluation', label: 'Evaluation', description: 'What to measure' },
  { id: 'advanced', label: 'Advanced', description: 'Optimizer settings' },
];

// OBJECTIVE_SOURCES removed - now handled by ObjectivesEditor component

const OPTIMIZABLE_FIELD_TYPES = [
  { value: 'prompt', label: 'Prompt', fieldType: 'text', strategy: 'llm_rewrite' },
  { value: 'model', label: 'Model', fieldType: 'enum', strategy: 'llm_rewrite' },
  { value: 'temperature', label: 'Temperature', fieldType: 'numeric', strategy: 'interpolate' },
  { value: 'tools', label: 'Tools', fieldType: 'list', strategy: 'add_remove' },
  { value: 'skills', label: 'Skills', fieldType: 'list', strategy: 'add_remove' },
];

const FALLBACK_MODELS: Model[] = [
  { value: 'openrouter:anthropic/claude-sonnet-4.5', label: 'Claude Sonnet 4.5', provider: 'openrouter' },
  { value: 'openrouter:anthropic/claude-opus-4.5', label: 'Claude Opus 4.5', provider: 'openrouter' },
  { value: 'openrouter:openai/gpt-4o', label: 'GPT-4o', provider: 'openrouter' },
];

const DEFAULT_OPTIMIZATION: OptimizationSettings = {
  enabled: false,
  maxIterations: 10,
  maxEvalRuns: 50,
  maxDurationHours: 4,
  trialsPerIteration: 3,
  checkpointInterval: 5,
  autoApproveThreshold: undefined,
  checkpointTimeoutHours: 24,
  initialMode: 'sequential',
  beamWidth: 3,
  plateauWindow: 3,
  plateauThreshold: 0.01,
  optimizerModel: 'openrouter:anthropic/claude-sonnet-4.5',
  goalDescription: '',
  directives: [],
};

const DEFAULT_HITL_CONFIG: HITLConfig = {
  strategy: 'bypass',
  mockResponses: [],
  bypassResponse: { action: 'approve' },
  llmModel: 'openai:gpt-4o-mini',
  llmPersona: 'helpful_reviewer',
  llmTemperature: 0.3,
};

export function EvalWizard({ open, onClose, onSuccess, editMode = false, evalId, initialData, startOptimizationMode = false }: EvalWizardProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Data loading
  const [agents, setAgents] = useState<Agent[]>([]);
  const [judges, setJudges] = useState<Agent[]>([]);
  const [models, setModels] = useState<Model[]>(FALLBACK_MODELS);
  const [evalFunctions, setEvalFunctions] = useState<EvalFunction[]>([]);
  const [agentVariables, setAgentVariables] = useState<AgentVariable[]>([]);
  const [loadingVariables, setLoadingVariables] = useState(false);
  const [agentVersions, setAgentVersions] = useState<ContentVersion[]>([]);
  const [agentHITL, setAgentHITL] = useState<AgentHITLInfo | null>(null);

  // Popover states
  const [agentPopoverOpen, setAgentPopoverOpen] = useState(false);
  const [judgePopoverOpen, setJudgePopoverOpen] = useState(false);
  const [functionPopoverOpen, setFunctionPopoverOpen] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    // Basics
    name: '',
    id: '',
    description: '',
    mode: 'standard' as 'standard' | 'optimization',

    // Agent & Scenarios
    agent: '',
    variableModes: {} as Record<string, VariableMode>,  // 'default' or 'scenario' per variable
    scenarioRows: [] as ScenarioRow[],  // Only contains 'scenario' mode variable values
    inputDefaults: {} as Record<string, string>,  // Default values for 'default' mode variables

    // Variants (standard mode)
    variants: [{
      name: 'baseline',
      model: 'openrouter:anthropic/claude-sonnet-4.5',
      temperature: 0,
      graphSettings: { parallelExecution: false, maxParallelTasks: 3 }
    }] as Variant[],

    // Objectives (optimization mode) - default to metric until evaluation method is configured
    objectives: [
      { name: 'latency', source: 'metric', source_key: 'latency_ms', direction: 'minimize' as const, weight: 1.0 }
    ] as Objective[],
    optimizableFields: OPTIMIZABLE_FIELD_TYPES.map(f => ({
      fieldPath: f.value,
      fieldType: f.fieldType,
      mutationStrategy: f.strategy,
      enabled: f.value === 'prompt'
    })) as OptimizableField[],
    paretoSelection: 'weighted_sum',

    // Judge
    judgeMode: 'none' as 'none' | 'llm' | 'function' | 'both',
    judge: '',
    passThreshold: 0.7,
    judgeTools: ['execute_python'] as string[],
    evalFunction: '',
    evalFunctionConfig: {} as Record<string, string>,

    // Simplified evaluation config (optimization mode)
    evaluationConfig: {
      correctnessFunction: null,
      qualityMode: 'none',
      optimizeLatency: false,
      optimizeCost: false,
      optimizeToolCalls: false,
      judgeModel: 'anthropic:claude-sonnet-4-5',
      judgePassThreshold: 0.7,
    } as EvaluationConfig,

    // Advanced
    metrics: ['latency_ms', 'total_tokens'] as string[],
    optimization: { ...DEFAULT_OPTIMIZATION },

    // HITL Strategy
    hitl: { ...DEFAULT_HITL_CONFIG },
  });

  // Get active steps based on mode
  const WIZARD_STEPS = useMemo(() => {
    return formData.mode === 'optimization' ? WIZARD_STEPS_OPTIMIZATION : WIZARD_STEPS_STANDARD;
  }, [formData.mode]);

  // Load agents, judges, models on open
  useEffect(() => {
    if (open) {
      fetchData();
    }
  }, [open]);

  // Populate form from initialData when in edit mode
  useEffect(() => {
    if (open && editMode && initialData) {
      console.log('Populating form from initialData:', initialData);

      // Parse scenarios from backend format
      // Each scenario is an object like { query: "...", date_override: "2025-12-08", ticker: "SPY" }
      const inputsConfig = initialData.inputs as { scenarios?: Array<Record<string, string>>; defaults?: Record<string, string> } | undefined;
      const inputScenarios = inputsConfig?.scenarios || [];
      const inputDefaults = inputsConfig?.defaults || {};

      // Derive variable modes from data:
      // - If a key is in defaults with non-empty value AND all scenarios have same/empty value -> 'default' mode
      // - If values vary across scenarios -> 'scenario' mode
      const allKeys = new Set<string>();
      inputScenarios.forEach(s => Object.keys(s).forEach(k => allKeys.add(k)));
      Object.keys(inputDefaults).forEach(k => allKeys.add(k));

      const variableModes: Record<string, VariableMode> = {};
      allKeys.forEach(key => {
        const defaultValue = inputDefaults[key];
        const scenarioValues = inputScenarios.map(s => s[key] || '');
        const uniqueScenarioValues = new Set(scenarioValues.filter(v => v));

        // If there's a default and all scenarios either use that default or are empty, it's 'default' mode
        // If scenario values vary (more than 1 unique value), it's 'scenario' mode
        if (defaultValue && uniqueScenarioValues.size <= 1) {
          variableModes[key] = 'default';
        } else if (uniqueScenarioValues.size > 1 || (uniqueScenarioValues.size === 1 && !defaultValue)) {
          variableModes[key] = 'scenario';
        } else {
          // Default to 'default' mode if we have a default value
          variableModes[key] = defaultValue ? 'default' : 'scenario';
        }
      });

      // Convert scenarios to rows - only include 'scenario' mode variable values
      const scenarioRows: ScenarioRow[] = inputScenarios.map(scenario => {
        const row: ScenarioRow = {};
        Object.entries(scenario).forEach(([k, v]) => {
          if (variableModes[k] === 'scenario') {
            row[k] = v;
          }
        });
        return row;
      });

      // Parse variants from backend format
      const backendVariants = (initialData.variants as Array<{
        name: string;
        model?: string;
        temperature?: number;
        agent_version?: number;
        graphSettings?: { parallelExecution: boolean; maxParallelTasks: number };
      }>) || [];
      const variants: Variant[] = backendVariants.map(v => ({
        name: v.name,
        model: v.model || 'openrouter:anthropic/claude-sonnet-4.5',
        temperature: v.temperature ?? 0,
        agentVersion: v.agent_version || null,
        graphSettings: v.graphSettings || { parallelExecution: false, maxParallelTasks: 3 },
      }));
      if (variants.length === 0) {
        variants.push({
          name: 'baseline',
          model: 'openrouter:anthropic/claude-sonnet-4.5',
          temperature: 0,
          graphSettings: { parallelExecution: false, maxParallelTasks: 3 },
        });
      }

      // Parse judge config
      const judgeConfig = initialData.judge as { agent?: string; model?: string; pass_threshold?: number; tools?: string[] } | undefined;

      // Parse eval functions
      const evalFuncs = (initialData.eval_functions as Array<{
        function?: string;
        config?: Record<string, string>;
      }>) || [];
      const firstFunc = evalFuncs[0];
      // Extract function ID from module path (e.g., "evals.functions.stock:fetch_stock_movement" -> "fetch_stock_movement")
      let evalFunctionId = '';
      if (firstFunc?.function) {
        const colonIdx = firstFunc.function.lastIndexOf(':');
        evalFunctionId = colonIdx >= 0 ? firstFunc.function.slice(colonIdx + 1) : firstFunc.function;
      }

      // Parse optimization config
      const optConfig = initialData.optimization as {
        enabled?: boolean;
        optimizable_fields?: Array<{ field_path: string; field_type: string; enabled?: boolean }>;
        objectives?: Array<{
          name: string;
          source: string;
          direction: string;
          weight: number;
          target_threshold?: number;
          required?: boolean;
          // Backend may use different field names for source_key
          source_key?: string;
          metric_name?: string;
          criterion_name?: string;
          function_name?: string;
        }>;
        pareto_selection?: string;
        exploration?: { initial_mode?: string; beam_width?: number; plateau_window?: number; plateau_threshold?: number };
        checkpoints?: { interval_iterations?: number; auto_approve_threshold?: number; timeout_hours?: number };
        limits?: { max_iterations?: number; max_eval_runs?: number; max_duration_hours?: number; max_cost_dollars?: number; trials_per_iteration?: number };
        optimizer_model?: string;
        goal_description?: string;
        directives?: string[];
      } | undefined;

      // Determine mode
      const isOptimization = optConfig?.enabled || false;

      // Parse optimizable fields
      let optimizableFields = OPTIMIZABLE_FIELD_TYPES.map(f => ({
        fieldPath: f.value,
        fieldType: f.fieldType,
        mutationStrategy: f.strategy,
        enabled: f.value === 'prompt',
      }));
      if (optConfig?.optimizable_fields) {
        const enabledFields = new Set(optConfig.optimizable_fields.map(f => f.field_path));
        optimizableFields = optimizableFields.map(f => ({
          ...f,
          enabled: enabledFields.has(f.fieldPath),
        }));
      }

      // Parse objectives - default to metric until evaluation method is configured
      let objectives: Objective[] = [{ name: 'latency', source: 'metric', source_key: 'latency_ms', direction: 'minimize', weight: 1.0 }];
      if (optConfig?.objectives) {
        objectives = optConfig.objectives.map(o => {
          // Map backend field names to source_key
          let sourceKey: string | undefined;
          if (o.metric_name) sourceKey = o.metric_name;
          else if (o.criterion_name) sourceKey = o.criterion_name;
          else if (o.function_name) sourceKey = o.function_name;
          else if (o.source_key) sourceKey = o.source_key;

          return {
            name: o.name,
            source: o.source as Objective['source'],
            source_key: sourceKey,
            direction: o.direction as 'maximize' | 'minimize',
            weight: o.weight,
            target_threshold: o.target_threshold,
            required: o.required ?? false,
            source_config: o.source_config,
          };
        });
      }

      // Parse HITL config
      const hitlBackend = initialData.hitl as {
        strategy?: 'bypass' | 'mock' | 'llm_responder';
        mock_responses?: Record<string, Record<string, unknown>>;
        bypass_response?: Record<string, unknown>;
        llm_config?: { model?: string; persona?: string; temperature?: number };
      } | undefined;

      let hitlConfig: HITLConfig = { ...DEFAULT_HITL_CONFIG };
      if (hitlBackend) {
        hitlConfig = {
          strategy: hitlBackend.strategy || 'bypass',
          bypassResponse: hitlBackend.bypass_response || { action: 'approve' },
          mockResponses: hitlBackend.mock_responses
            ? Object.entries(hitlBackend.mock_responses).map(([id, response]) => ({
                decisionPointId: id,
                response,
              }))
            : [],
          llmModel: hitlBackend.llm_config?.model || 'openai:gpt-4o-mini',
          llmPersona: hitlBackend.llm_config?.persona || 'helpful_reviewer',
          llmTemperature: hitlBackend.llm_config?.temperature ?? 0.3,
        };
      }

      // Derive judgeMode from judge and evalFunction
      const hasJudge = Boolean(judgeConfig?.agent);
      const hasFunction = Boolean(evalFunctionId);
      let judgeMode: 'none' | 'llm' | 'function' | 'both' = 'none';
      if (hasJudge && hasFunction) {
        judgeMode = 'both';
      } else if (hasJudge) {
        judgeMode = 'llm';
      } else if (hasFunction) {
        judgeMode = 'function';
      }

      // Convert objectives to evaluationConfig for the new UI
      const evaluationConfig = objectivesToConfig(objectives);
      evaluationConfig.judgeAgent = judgeConfig?.agent;
      evaluationConfig.judgeModel = judgeConfig?.model || 'anthropic:claude-sonnet-4-5';
      evaluationConfig.judgePassThreshold = judgeConfig?.pass_threshold ?? 0.7;

      setFormData({
        name: (initialData.name as string) || '',
        id: (initialData.id as string) || '',
        description: (initialData.description as string) || '',
        mode: isOptimization ? 'optimization' : 'standard',
        agent: (initialData.agent as string) || '',
        variableModes,
        scenarioRows,
        inputDefaults,
        variants,
        objectives,
        optimizableFields,
        paretoSelection: optConfig?.pareto_selection || 'weighted_sum',
        judgeMode,
        judge: judgeConfig?.agent || '',
        passThreshold: judgeConfig?.pass_threshold ?? 0.7,
        judgeTools: judgeConfig?.tools || ['execute_python'],
        evalFunction: evalFunctionId,
        evalFunctionConfig: firstFunc?.config || {},
        evaluationConfig,
        metrics: (initialData.metrics as string[]) || ['latency_ms', 'total_tokens'],
        optimization: {
          enabled: isOptimization,
          maxIterations: optConfig?.limits?.max_iterations ?? 10,
          maxEvalRuns: optConfig?.limits?.max_eval_runs ?? 50,
          maxDurationHours: optConfig?.limits?.max_duration_hours ?? 4,
          maxCostDollars: optConfig?.limits?.max_cost_dollars,
          trialsPerIteration: optConfig?.limits?.trials_per_iteration ?? 3,
          checkpointInterval: optConfig?.checkpoints?.interval_iterations ?? 5,
          autoApproveThreshold: optConfig?.checkpoints?.auto_approve_threshold,
          checkpointTimeoutHours: optConfig?.checkpoints?.timeout_hours ?? 24,
          initialMode: (optConfig?.exploration?.initial_mode as 'sequential' | 'parallel') || 'sequential',
          beamWidth: optConfig?.exploration?.beam_width ?? 3,
          plateauWindow: optConfig?.exploration?.plateau_window ?? 3,
          plateauThreshold: optConfig?.exploration?.plateau_threshold ?? 0.01,
          optimizerModel: optConfig?.optimizer_model || 'openrouter:anthropic/claude-sonnet-4.5',
          goalDescription: optConfig?.goal_description || '',
          directives: optConfig?.directives || [],
        },
        hitl: hitlConfig,
      });

      // Fetch agent variables if agent is set
      if (initialData.agent) {
        // Find the agent's UUID by path
        const agent = agents.find(a => a.path === initialData.agent);
        if (agent) {
          fetchAgentVariables(agent.id);
        }
      }
    }
  }, [open, editMode, initialData, agents]);

  // Auto-adjust objectives when judge/eval function configuration changes
  // This prevents mismatch between objective source and available evaluation methods
  useEffect(() => {
    if (formData.mode !== 'optimization') return;

    const hasJudge = formData.judgeMode === 'llm' || formData.judgeMode === 'both';
    const hasEvalFunction = !!formData.evalFunction;  // Must have actual function selected

    // Check if any objectives need adjustment
    const needsUpdate = formData.objectives.some(obj => {
      // Judge-based objectives require a judge
      if ((obj.source === 'judge_score' || obj.source === 'judge_criterion') && !hasJudge) {
        return true;
      }
      // Eval function objectives require an eval function
      if (obj.source === 'eval_function' && !hasEvalFunction) {
        return true;
      }
      return false;
    });

    if (!needsUpdate) return;

    // Determine the best default source based on configuration
    const getDefaultSource = (): Objective['source'] => {
      if (hasJudge) return 'judge_score';
      if (hasEvalFunction) return 'eval_function';
      return 'metric';
    };

    const defaultSource = getDefaultSource();

    setFormData(prev => ({
      ...prev,
      objectives: prev.objectives.map(obj => {
        // Auto-fix judge-based objectives when no judge
        if ((obj.source === 'judge_score' || obj.source === 'judge_criterion') && !hasJudge) {
          return { ...obj, source: defaultSource, source_key: defaultSource === 'metric' ? 'latency_ms' : undefined };
        }
        // Auto-fix eval function objectives when no eval function
        if (obj.source === 'eval_function' && !hasEvalFunction) {
          return { ...obj, source: defaultSource, source_key: defaultSource === 'metric' ? 'latency_ms' : undefined };
        }
        return obj;
      }),
    }));
  }, [formData.mode, formData.judgeMode, formData.judge, formData.evalFunction]);

  const fetchData = async () => {
    try {
      const [agentsRes, judgesRes] = await Promise.all([
        fetch('/api/agents', { headers: getAuthHeaders(), credentials: 'include' }),
        fetch('/api/judges', { headers: getAuthHeaders(), credentials: 'include' }),
      ]);

      if (agentsRes.ok) {
        setAgents(await agentsRes.json());
      }
      if (judgesRes.ok) {
        setJudges(await judgesRes.json());
      }

      try {
        const modelsData = await listModels();
        if (modelsData?.length > 0) {
          setModels(modelsData);
        }
      } catch {
        console.warn('Failed to fetch models');
      }

      try {
        const funcs = await evalFunctionApi.list();
        setEvalFunctions(funcs);
      } catch {
        console.warn('Failed to fetch eval functions');
      }
    } catch (err) {
      console.error('Failed to fetch data:', err);
    }
  };

  // Fetch agent parameters (template variables) when agent is selected
  const fetchAgentVariables = async (agentId: string) => {
    try {
      setLoadingVariables(true);
      // Use agent ID (UUID) for more reliable lookup
      const res = await fetch(`/api/agents/${encodeURIComponent(agentId)}/parameters`, {
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        // Map parameters to our AgentVariable format
        const variables = (data.parameters || []).map((p: { name: string; required?: boolean; description?: string; default?: string }) => ({
          name: p.name,
          type: 'string',
          required: p.required ?? false,
          description: p.description,
          default: p.default,
        }));
        setAgentVariables(variables);

        // Extract HITL info from the response
        if (data.hitl) {
          setAgentHITL({
            enabled: data.hitl.enabled || false,
            decision_points: data.hitl.decision_points || [],
            has_hitl_tool: data.hitl.has_hitl_tool || false,
            has_hitl_nodes: data.hitl.has_hitl_nodes || false,
          });
        } else {
          setAgentHITL(null);
        }

        // Initialize variable modes for new variables if not already set
        if (variables.length > 0) {
          setFormData(prev => {
            const newVariableModes = { ...prev.variableModes };
            const newInputDefaults = { ...prev.inputDefaults };
            let hasNewScenarioVars = false;

            for (const v of variables) {
              if (!(v.name in newVariableModes)) {
                // Smart defaults: date_override should be per-scenario, others default
                if (v.name === 'date_override') {
                  newVariableModes[v.name] = 'scenario';
                  hasNewScenarioVars = true;
                } else {
                  newVariableModes[v.name] = 'default';
                  newInputDefaults[v.name] = v.default || '';
                }
              } else if (newVariableModes[v.name] === 'scenario') {
                hasNewScenarioVars = true;
              }
            }

            // Create initial scenario row if we have scenario variables and no rows yet
            let newScenarioRows = prev.scenarioRows;
            if (hasNewScenarioVars && prev.scenarioRows.length === 0) {
              const emptyRow: ScenarioRow = {};
              for (const v of variables) {
                if (newVariableModes[v.name] === 'scenario') {
                  emptyRow[v.name] = '';
                }
              }
              newScenarioRows = [emptyRow];
            }

            return {
              ...prev,
              variableModes: newVariableModes,
              inputDefaults: newInputDefaults,
              scenarioRows: newScenarioRows,
            };
          });
        }
      } else {
        setAgentVariables([]);
        setAgentHITL(null);
      }

      // Fetch agent versions for version selector
      try {
        const versionsResult = await getAgentVersions(agentId);
        console.log('Agent versions loaded:', agentId, versionsResult.versions);
        setAgentVersions(versionsResult.versions || []);
      } catch (err) {
        console.error('Failed to fetch agent versions:', err);
        setAgentVersions([]);
      }
    } catch (err) {
      console.error('Failed to fetch agent parameters:', err);
      setAgentVariables([]);
      setAgentVersions([]);
      setAgentHITL(null);
    } finally {
      setLoadingVariables(false);
    }
  };

  // Helper to check if agent uses HITL
  const agentUsesHITL = agentHITL && (
    agentHITL.enabled ||
    agentHITL.has_hitl_tool ||
    agentHITL.has_hitl_nodes ||
    agentHITL.decision_points.length > 0
  );

  // Helper to get agent name from path
  const getAgentName = (agentPath: string): string => {
    const agent = agents.find(a => a.path === agentPath);
    return agent?.name || agentPath.split('/').pop()?.replace('.md', '') || agentPath;
  };

  const handleClose = () => {
    setCurrentStep(0);
    setError(null);
    setAgentVariables([]);
    setAgentVersions([]);
    setAgentHITL(null);
    setFormData({
      name: '',
      id: '',
      description: '',
      mode: 'standard',
      agent: '',
      variableModes: {} as Record<string, VariableMode>,
      scenarioRows: [] as ScenarioRow[],
      inputDefaults: {},
      variants: [{
        name: 'baseline',
        model: 'openrouter:anthropic/claude-sonnet-4.5',
        temperature: 0,
        graphSettings: { parallelExecution: false, maxParallelTasks: 3 }
      }],
      objectives: [{ name: 'latency', source: 'metric', source_key: 'latency_ms', direction: 'minimize', weight: 1.0 }],
      optimizableFields: OPTIMIZABLE_FIELD_TYPES.map(f => ({
        fieldPath: f.value,
        fieldType: f.fieldType,
        mutationStrategy: f.strategy,
        enabled: f.value === 'prompt'
      })),
      paretoSelection: 'weighted_sum',
      judgeMode: 'none',
      judge: '',
      passThreshold: 0.7,
      judgeTools: ['execute_python'],
      evalFunction: '',
      evalFunctionConfig: {},
      evaluationConfig: {
        correctnessFunction: null,
        qualityMode: 'none',
        optimizeLatency: false,
        optimizeCost: false,
        optimizeToolCalls: false,
        judgeModel: 'anthropic:claude-sonnet-4-5',
        judgePassThreshold: 0.7,
      },
      metrics: ['latency_ms', 'total_tokens'],
      optimization: { ...DEFAULT_OPTIMIZATION },
      hitl: { ...DEFAULT_HITL_CONFIG },
    });
    onClose();
  };

  const generateId = (name: string) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') + '-v1';
  };

  // Navigation
  const canGoNext = () => {
    switch (currentStep) {
      case 0: // Basics
        return formData.name.length > 0;
      case 1: // Agent
        return formData.agent.length > 0;
      case 2: // Evaluation (optimization) or Variants (standard)
        if (formData.mode === 'optimization') {
          // EvaluationSetup: need at least some evaluation configured
          const config = formData.evaluationConfig;
          const hasEvaluation = config.correctnessFunction ||
                               config.qualityMode !== 'none' ||
                               config.optimizeLatency ||
                               config.optimizeCost ||
                               config.optimizeToolCalls;
          return hasEvaluation && formData.optimizableFields.some(f => f.enabled);
        }
        return formData.variants.length > 0;
      case 3: // Advanced (optimization) or Judge (standard)
        if (formData.mode === 'optimization') {
          return true; // Advanced is always valid
        }
        // Standard mode: Judge validation
        if (formData.judgeMode === 'llm') {
          return Boolean(formData.judge);
        } else if (formData.judgeMode === 'function') {
          return Boolean(formData.evalFunction);
        } else if (formData.judgeMode === 'both') {
          return Boolean(formData.judge) && Boolean(formData.evalFunction);
        }
        return true; // 'none' mode is always valid
      case 4: // Advanced (standard mode only)
        return true;
      default:
        return false;
    }
  };

  const goNext = () => {
    if (currentStep < WIZARD_STEPS.length - 1 && canGoNext()) {
      setCurrentStep(currentStep + 1);
    }
  };

  const goBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Combine agent variables with any extra variables from loaded data (like date_override)
  const allVariables = useMemo(() => {
    const agentVarNames = new Set(agentVariables.map(v => v.name));
    const extraVars: AgentVariable[] = [];

    // Add any variables from variableModes that aren't in agentVariables
    Object.keys(formData.variableModes).forEach(name => {
      if (!agentVarNames.has(name)) {
        extraVars.push({
          name,
          type: 'string',
          required: false,
          description: name === 'date_override' ? 'Date override for time-based testing' : undefined,
        });
      }
    });

    return [...agentVariables, ...extraVars];
  }, [agentVariables, formData.variableModes]);

  // Get variables that are in 'scenario' mode (these become table columns)
  const scenarioVariables = useMemo(() =>
    allVariables.filter(v => formData.variableModes[v.name] === 'scenario'),
    [allVariables, formData.variableModes]
  );

  // Get variables that are in 'default' mode
  const defaultVariables = useMemo(() =>
    allVariables.filter(v => formData.variableModes[v.name] === 'default'),
    [allVariables, formData.variableModes]
  );

  // Update variable mode (default vs scenario)
  const setVariableMode = (varName: string, mode: VariableMode) => {
    setFormData(prev => {
      const newVariableModes = { ...prev.variableModes, [varName]: mode };
      let newInputDefaults = { ...prev.inputDefaults };
      let newScenarioRows = prev.scenarioRows;

      if (mode === 'default') {
        // Moving to default mode - get value from first scenario row if exists
        const firstRowValue = prev.scenarioRows[0]?.[varName] || '';
        newInputDefaults[varName] = firstRowValue;
        // Remove from scenario rows
        newScenarioRows = prev.scenarioRows.map(row => {
          const { [varName]: _, ...rest } = row;
          return rest;
        });
      } else {
        // Moving to scenario mode - copy default to first scenario row
        const defaultValue = prev.inputDefaults[varName] || '';
        const { [varName]: _, ...restDefaults } = newInputDefaults;
        newInputDefaults = restDefaults;
        // Add to scenario rows
        if (prev.scenarioRows.length === 0) {
          newScenarioRows = [{ [varName]: defaultValue }];
        } else {
          newScenarioRows = prev.scenarioRows.map((row, i) => ({
            ...row,
            [varName]: i === 0 ? defaultValue : '',
          }));
        }
      }

      return {
        ...prev,
        variableModes: newVariableModes,
        inputDefaults: newInputDefaults,
        scenarioRows: newScenarioRows,
      };
    });
  };

  // Form helpers - Scenario Rows
  const addScenarioRow = () => {
    const emptyRow: ScenarioRow = {};
    for (const v of scenarioVariables) {
      emptyRow[v.name] = '';
    }
    setFormData(prev => ({
      ...prev,
      scenarioRows: [...prev.scenarioRows, emptyRow]
    }));
  };

  const cloneScenarioRow = (index: number) => {
    setFormData(prev => {
      const original = prev.scenarioRows[index];
      const cloned = { ...original };
      const newRows = [...prev.scenarioRows];
      newRows.splice(index + 1, 0, cloned);
      return { ...prev, scenarioRows: newRows };
    });
  };

  const removeScenarioRow = (index: number) => {
    setFormData(prev => ({
      ...prev,
      scenarioRows: prev.scenarioRows.filter((_, i) => i !== index)
    }));
  };

  const updateScenarioValue = (rowIndex: number, fieldName: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      scenarioRows: prev.scenarioRows.map((row, i) =>
        i === rowIndex
          ? { ...row, [fieldName]: value }
          : row
      )
    }));
  };

  // Date range generator state
  const [dateRangeStart, setDateRangeStart] = useState('2025-01-01');
  const [dateRangeEnd, setDateRangeEnd] = useState('2025-01-31');
  const [dateInterval, setDateInterval] = useState<'daily' | 'weekdays' | 'weekly'>('weekly');

  // Generate date range scenarios
  const generateDateRange = () => {
    const start = new Date(dateRangeStart);
    const end = new Date(dateRangeEnd);
    const newScenarios: ScenarioRow[] = [];

    const current = new Date(start);
    while (current <= end) {
      // Skip weekends if weekdays only
      if (dateInterval === 'weekdays' && (current.getDay() === 0 || current.getDay() === 6)) {
        current.setDate(current.getDate() + 1);
        continue;
      }

      const row: ScenarioRow = {
        date_override: current.toISOString().split('T')[0],
      };
      // Add empty values for other scenario variables
      for (const v of scenarioVariables) {
        if (v.name !== 'date_override') {
          row[v.name] = '';
        }
      }
      newScenarios.push(row);

      // Increment based on interval
      if (dateInterval === 'daily' || dateInterval === 'weekdays') {
        current.setDate(current.getDate() + 1);
      } else {
        current.setDate(current.getDate() + 7);
      }
    }

    setFormData(prev => ({
      ...prev,
      scenarioRows: [...prev.scenarioRows, ...newScenarios]
    }));
  };

  // Import state
  const [importMode, setImportMode] = useState<'csv' | 'json'>('csv');
  const [importText, setImportText] = useState('');

  // Parse import data
  const parseImport = () => {
    try {
      let parsed: Record<string, string>[] = [];

      if (importMode === 'csv') {
        const lines = importText.trim().split('\n');
        if (lines.length < 2) {
          alert('CSV must have header row and at least one data row');
          return;
        }
        const headers = lines[0].split(',').map(h => h.trim());
        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].split(',').map(v => v.trim());
          const row: Record<string, string> = {};
          headers.forEach((h, idx) => {
            row[h] = values[idx] || '';
          });
          parsed.push(row);
        }
      } else {
        parsed = JSON.parse(importText);
        if (!Array.isArray(parsed)) {
          parsed = [parsed];
        }
      }

      const newScenarios: ScenarioRow[] = parsed.map(row => {
        const scenarioRow: ScenarioRow = {};
        // Only include scenario-mode variables
        for (const v of scenarioVariables) {
          scenarioRow[v.name] = row[v.name] || '';
        }
        return scenarioRow;
      });

      setFormData(prev => ({
        ...prev,
        scenarioRows: [...prev.scenarioRows, ...newScenarios]
      }));
      setImportText('');
    } catch (e) {
      alert(`Parse error: ${e}`);
    }
  };

  // Show prompt preview state
  const [showPromptPreview, setShowPromptPreview] = useState(false);

  // Deprecated: Cross-product dimension helpers (kept for reference but not used in new UI)
  const removeDimensionValue = (dimension: string, index: number) => {
    setFormData(prev => ({
      ...prev,
      scenarioDimensions: {
        ...prev.scenarioDimensions,
        [dimension]: (prev.scenarioDimensions[dimension] || []).filter((_, i) => i !== index)
      }
    }));
  };

  // Cartesian product helper for cross-product mode
  const cartesianProduct = (dimensions: Record<string, string[]>): Record<string, string>[] => {
    const keys = Object.keys(dimensions).filter(k => dimensions[k].length > 0);
    if (keys.length === 0) return [];

    return keys.reduce((acc, key) => {
      const values = dimensions[key];
      if (acc.length === 0) {
        return values.map(v => ({ [key]: v }));
      }
      return acc.flatMap(obj => values.map(v => ({ ...obj, [key]: v })));
    }, [] as Record<string, string>[]);
  };

  const addVariant = () => {
    const num = formData.variants.length + 1;
    setFormData(prev => ({
      ...prev,
      variants: [...prev.variants, {
        name: `variant-${num}`,
        model: 'openrouter:anthropic/claude-sonnet-4.5',
        temperature: 0,
        graphSettings: { parallelExecution: false, maxParallelTasks: 3 }
      }]
    }));
  };

  const removeVariant = (index: number) => {
    if (formData.variants.length <= 1) return;
    setFormData(prev => ({
      ...prev,
      variants: prev.variants.filter((_, i) => i !== index)
    }));
  };

  const updateVariant = (index: number, field: keyof Variant, value: unknown) => {
    setFormData(prev => ({
      ...prev,
      variants: prev.variants.map((v, i) => i === index ? { ...v, [field]: value } : v)
    }));
  };

  // Objectives are now managed by ObjectivesEditor component
  const handleObjectivesChange = (newObjectives: Objective[]) => {
    setFormData(prev => ({ ...prev, objectives: newObjectives }));
  };

  const toggleOptimizableField = (fieldPath: string) => {
    setFormData(prev => ({
      ...prev,
      optimizableFields: prev.optimizableFields.map(f =>
        f.fieldPath === fieldPath ? { ...f, enabled: !f.enabled } : f
      )
    }));
  };

  // Submit
  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      // Validation for optimization mode
      if (formData.mode === 'optimization') {
        // Check if any objective uses judge_score but no judge is configured
        const hasJudgeScoreObjective = formData.objectives.some(o => o.source === 'judge_score');
        const hasJudgeCriterionObjective = formData.objectives.some(o => o.source === 'judge_criterion');
        const hasJudgeConfigured = formData.judgeMode === 'llm' || formData.judgeMode === 'both';
        const hasJudgeAgent = Boolean(formData.judge || formData.evaluationConfig.judgeAgent);

        if ((hasJudgeScoreObjective || hasJudgeCriterionObjective) && !hasJudgeConfigured) {
          setError(
            `Your objectives include "${hasJudgeScoreObjective ? 'Judge Score' : 'Judge Criterion'}" but no judge is configured. ` +
            `Either add a judge in the Evaluation Method section, or change your objective source to "Eval Function" or "Metric".`
          );
          setLoading(false);
          return;
        }

        // Check if judge mode is configured but no judge agent is selected
        if (hasJudgeConfigured && !hasJudgeAgent) {
          setError(
            'LLM Judge is selected for quality scoring but no judge agent is selected. ' +
            'Please select a judge agent in the Quality Scoring section.'
          );
          setLoading(false);
          return;
        }

        // Check if any objective uses eval_function but no eval function is configured
        const evalFunctionObjectives = formData.objectives.filter(o => o.source === 'eval_function');
        if (evalFunctionObjectives.length > 0 && !formData.evalFunction) {
          setError(
            'Your objectives include "Eval Function" but no eval function is configured. ' +
            'Either add an eval function in the Evaluation Method section, or change your objective source.'
          );
          setLoading(false);
          return;
        }
      }

      // Convert scenarios from UI format to backend format
      // Each scenario row contains per-scenario values, which get merged with defaults
      // Backend receives: scenarios[] where each scenario has all variable values
      const scenarios: Record<string, string>[] = formData.scenarioRows
        .map(row => {
          const scenario: Record<string, string> = {};
          // Add all per-scenario values from the row
          for (const [key, value] of Object.entries(row)) {
            if (value) {
              scenario[key] = value;
            }
          }
          return scenario;
        })
        .filter(s => Object.keys(s).length > 0);

      // If no scenarios, create one empty scenario (will use defaults only)
      if (scenarios.length === 0 && Object.keys(formData.inputDefaults).some(k => formData.inputDefaults[k])) {
        scenarios.push({});
      }

      const config: Record<string, unknown> = {
        name: formData.name,
        id: formData.id || generateId(formData.name),
        description: formData.description,
        agent: formData.agent,
        inputs: {
          scenarios,
          ...(Object.keys(formData.inputDefaults).length > 0 ? { defaults: formData.inputDefaults } : {}),
        },
        metrics: formData.metrics,
      };

      if (formData.mode === 'optimization') {
        // Optimization mode config
        config.optimization = {
          enabled: true,
          optimizable_fields: formData.optimizableFields
            .filter(f => f.enabled)
            .map(f => ({
              field_path: f.fieldPath,
              field_type: f.fieldType,
              mutation_strategy: f.mutationStrategy,
            })),
          objectives: formData.objectives.map(o => {
            const objective: Record<string, unknown> = {
              name: o.name,
              source: o.source,
              direction: o.direction,
              weight: o.required ? 0 : o.weight, // Required objectives have 0 weight
            };

            // Map source_key to the appropriate backend field name
            if (o.source_key) {
              if (o.source === 'metric') {
                objective.metric_name = o.source_key;
              } else if (o.source === 'judge_criterion') {
                objective.criterion_name = o.source_key;
              } else if (o.source === 'eval_function') {
                objective.function_name = o.source_key;
              }
            }

            if (o.target_threshold !== undefined) {
              objective.target_threshold = o.target_threshold;
            }

            // Mark as required (constraint vs objective)
            if (o.required) {
              objective.required = true;
            }

            // Include source_config for eval function parameters
            if (o.source_config && Object.keys(o.source_config).length > 0) {
              objective.source_config = o.source_config;
            }

            return objective;
          }),
          pareto_selection: formData.paretoSelection,
          exploration: {
            initial_mode: formData.optimization.initialMode,
            beam_width: formData.optimization.beamWidth,
            plateau_window: formData.optimization.plateauWindow,
            plateau_threshold: formData.optimization.plateauThreshold,
          },
          checkpoints: {
            interval_iterations: formData.optimization.checkpointInterval,
            auto_approve_threshold: formData.optimization.autoApproveThreshold,
            timeout_hours: formData.optimization.checkpointTimeoutHours,
          },
          limits: {
            max_iterations: formData.optimization.maxIterations,
            max_eval_runs: formData.optimization.maxEvalRuns,
            max_duration_hours: formData.optimization.maxDurationHours,
            max_cost_dollars: formData.optimization.maxCostDollars,
            trials_per_iteration: formData.optimization.trialsPerIteration,
          },
          optimizer_model: formData.optimization.optimizerModel,
          // User guidance for the optimizer
          goal_description: formData.optimization.goalDescription || undefined,
          directives: formData.optimization.directives.length > 0
            ? formData.optimization.directives.filter(d => d.trim() !== '')
            : undefined,
        };
      } else {
        // Standard mode with variants
        config.variants = formData.variants.map(v => {
          const variant: Record<string, unknown> = {
            name: v.name,
            model: v.model,
            temperature: v.temperature,
            graphSettings: v.graphSettings,
          };
          // Only include agent_version if explicitly set (not null/undefined)
          if (v.agentVersion !== null && v.agentVersion !== undefined) {
            variant.agent_version = v.agentVersion;
          }
          return variant;
        });
      }

      if (formData.judge) {
        config.judge = {
          agent: formData.judge,
          model: formData.evaluationConfig.judgeModel,
          pass_threshold: formData.passThreshold,
          tools: formData.judgeTools,
        };
      }

      if (formData.evalFunction) {
        const func = evalFunctions.find(f => f.id === formData.evalFunction);
        if (func) {
          config.eval_functions = [{
            name: func.name,
            function: func.id,
            config: Object.keys(formData.evalFunctionConfig).length > 0
              ? formData.evalFunctionConfig
              : undefined,
          }];
        }
      }

      // Add HITL strategy configuration
      if (formData.hitl.strategy !== 'bypass' || formData.hitl.mockResponses.length > 0) {
        const hitlConfig: Record<string, unknown> = {
          strategy: formData.hitl.strategy,
        };

        if (formData.hitl.strategy === 'bypass') {
          hitlConfig.bypass_response = formData.hitl.bypassResponse;
        } else if (formData.hitl.strategy === 'mock') {
          // Convert mock responses array to object keyed by decision point ID
          const mockResponses: Record<string, Record<string, unknown>> = {};
          formData.hitl.mockResponses.forEach(mr => {
            if (mr.decisionPointId) {
              mockResponses[mr.decisionPointId] = mr.response;
            }
          });
          hitlConfig.mock_responses = mockResponses;
        } else if (formData.hitl.strategy === 'llm_responder') {
          hitlConfig.llm_config = {
            model: formData.hitl.llmModel,
            persona: formData.hitl.llmPersona,
            temperature: formData.hitl.llmTemperature,
          };
        }

        config.hitl = hitlConfig;
      }

      if (startOptimizationMode && evalId) {
        // Start optimization mode: save config changes then start optimization
        await updateEval(evalId, { config });

        // Start the optimization
        const res = await fetch(`/api/evals/${evalId}/optimization/start`, {
          method: 'POST',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({}),
        });

        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.detail || 'Failed to start optimization');
        }
      } else if (editMode && evalId) {
        await updateEval(evalId, { config });
      } else {
        await createEval({ config });
      }

      onSuccess?.();
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save evaluation');
    } finally {
      setLoading(false);
    }
  };

  // Render step content
  const renderStep = () => {
    switch (currentStep) {
      case 0: // Basics
        return (
          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={e => {
                  const name = e.target.value;
                  setFormData(prev => ({
                    ...prev,
                    name,
                    id: prev.id || generateId(name)
                  }));
                }}
                placeholder="My Evaluation"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="What does this evaluation test?"
                rows={2}
              />
            </div>

            <Separator />

            <div className="space-y-4">
              <Label>Evaluation Mode</Label>
              <div className="grid grid-cols-2 gap-4">
                <Card
                  className={cn(
                    "cursor-pointer transition-all",
                    formData.mode === 'standard' ? "border-primary bg-primary/5" : "hover:border-muted-foreground"
                  )}
                  onClick={() => setFormData(prev => ({ ...prev, mode: 'standard' }))}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-2">
                      <Beaker className="h-5 w-5" />
                      <CardTitle className="text-base">Standard Eval</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <CardDescription>
                      Run A/B tests with fixed variants. Compare different models, prompts, or settings.
                    </CardDescription>
                  </CardContent>
                </Card>

                <Card
                  className={cn(
                    "cursor-pointer transition-all",
                    formData.mode === 'optimization' ? "border-primary bg-primary/5" : "hover:border-muted-foreground"
                  )}
                  onClick={() => setFormData(prev => ({ ...prev, mode: 'optimization' }))}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-2">
                      <Sparkles className="h-5 w-5" />
                      <CardTitle className="text-base">Optimization Mode</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <CardDescription>
                      Iteratively improve prompts and configs toward objectives with human checkpoints.
                    </CardDescription>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        );

      case 1: // Agent & Scenarios
        return (
          <div className="space-y-6">
            <div className="space-y-2">
              <Label>Select Agent *</Label>
              <Popover open={agentPopoverOpen} onOpenChange={setAgentPopoverOpen} modal={true}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start">
                    {formData.agent ? getAgentName(formData.agent) : 'Choose an agent...'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[400px] p-0">
                  <Command
                    filter={(value, search) => {
                      if (!search) return 1;
                      const searchLower = search.toLowerCase();
                      return value.toLowerCase().includes(searchLower) ? 1 : 0;
                    }}
                  >
                    <CommandInput placeholder="Search agents..." />
                    <CommandList>
                      <CommandEmpty>No agents found.</CommandEmpty>
                      <CommandGroup>
                        {agents.map(agent => (
                          <CommandItem
                            key={agent.id}
                            value={`${agent.name} ${agent.path}`}
                            onSelect={() => {
                              setFormData(prev => ({ ...prev, agent: agent.path }));
                              fetchAgentVariables(agent.id);  // Use UUID for API call
                              setAgentPopoverOpen(false);
                            }}
                          >
                            <div>
                              <p className="font-medium">{agent.name}</p>
                              <p className="text-xs text-muted-foreground">{agent.path}</p>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {formData.mode === 'optimization' && (
              <>
                <Separator />
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <Label>Fields to Optimize</Label>
                      <p className="text-xs text-muted-foreground mt-1">
                        Select which parts of the agent config can be modified
                      </p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {OPTIMIZABLE_FIELD_TYPES.map(field => {
                      const isEnabled = formData.optimizableFields.find(f => f.fieldPath === field.value)?.enabled;
                      return (
                        <div key={field.value} className="flex items-center justify-between p-3 border rounded-lg">
                          <div>
                            <p className="font-medium">{field.label}</p>
                            <p className="text-xs text-muted-foreground">{field.strategy.replace('_', ' ')}</p>
                          </div>
                          <Switch
                            checked={isEnabled}
                            onCheckedChange={() => toggleOptimizableField(field.value)}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            )}

            <Separator />

            {/* Input Scenarios - Streamlined Variable Configuration */}
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <div>
                  <Label>Template Variables</Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    For each variable, choose whether it's the same for all scenarios or varies per scenario
                  </p>
                </div>
              </div>

              {/* Loading state */}
              {loadingVariables && (
                <div className="text-sm text-muted-foreground flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading agent parameters...
                </div>
              )}

              {/* No agent selected */}
              {!formData.agent && !loadingVariables && (
                <Card className="bg-muted/50">
                  <CardContent className="py-6 text-center">
                    <p className="text-sm text-muted-foreground">
                      Select an agent above to configure scenarios
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Agent selected but no parameters */}
              {formData.agent && !loadingVariables && allVariables.length === 0 && (
                <Card className="bg-muted/50">
                  <CardContent className="py-3">
                    <p className="text-sm text-muted-foreground">
                      This agent has no configurable template variables.
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Variable Configuration - Core UI */}
              {formData.agent && !loadingVariables && allVariables.length > 0 && (
                <div className="space-y-3">
                  {allVariables.map(v => {
                    const mode = formData.variableModes[v.name] || 'default';
                    return (
                      <div key={v.name} className="border rounded-lg p-3">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="flex items-center gap-2 flex-1">
                            <Badge variant="outline" className="font-mono text-xs">
                              {v.name}
                            </Badge>
                            {v.required && (
                              <span className="text-xs text-destructive font-medium">Required</span>
                            )}
                          </div>
                          <Select
                            value={mode}
                            onValueChange={(value: VariableMode) => setVariableMode(v.name, value)}
                          >
                            <SelectTrigger className="w-[130px] h-8">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="default">
                                <span className="flex items-center gap-2">
                                  <span className="w-2 h-2 bg-blue-500 rounded-full" />
                                  Default
                                </span>
                              </SelectItem>
                              <SelectItem value="scenario">
                                <span className="flex items-center gap-2">
                                  <span className="w-2 h-2 bg-purple-500 rounded-full" />
                                  Per Scenario
                                </span>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Default mode: show single input */}
                        {mode === 'default' && (
                          <div className="pl-3 border-l-2 border-blue-200">
                            <Input
                              value={formData.inputDefaults[v.name] || ''}
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                inputDefaults: { ...prev.inputDefaults, [v.name]: e.target.value }
                              }))}
                              placeholder={`Value for ${v.name}`}
                              className="h-8"
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                              This value is used for all scenarios
                            </p>
                          </div>
                        )}

                        {/* Scenario mode: show info */}
                        {mode === 'scenario' && (
                          <div className="pl-3 border-l-2 border-purple-200">
                            <p className="text-xs text-muted-foreground">
                              Configure values in the Scenarios table below
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Scenarios Table - Only shown if there are "scenario" mode variables */}
              {formData.agent && !loadingVariables && scenarioVariables.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-sm">Scenarios</Label>
                      <p className="text-xs text-muted-foreground">
                        {formData.scenarioRows.length} scenario{formData.scenarioRows.length !== 1 ? 's' : ''}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      {/* Date Range Generator - only if date_override is a scenario variable */}
                      {scenarioVariables.some(v => v.name === 'date_override') && (
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" size="sm">
                              <Calendar className="h-4 w-4 mr-1" />
                              Date Range
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-72">
                            <div className="space-y-3">
                              <p className="font-medium text-sm">Generate Date Range</p>
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <Label className="text-xs">Start</Label>
                                  <Input
                                    type="date"
                                    value={dateRangeStart}
                                    onChange={(e) => setDateRangeStart(e.target.value)}
                                  />
                                </div>
                                <div>
                                  <Label className="text-xs">End</Label>
                                  <Input
                                    type="date"
                                    value={dateRangeEnd}
                                    onChange={(e) => setDateRangeEnd(e.target.value)}
                                  />
                                </div>
                              </div>
                              <div>
                                <Label className="text-xs">Interval</Label>
                                <Select value={dateInterval} onValueChange={(v: 'daily' | 'weekdays' | 'weekly') => setDateInterval(v)}>
                                  <SelectTrigger className="h-8">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="daily">Daily</SelectItem>
                                    <SelectItem value="weekdays">Weekdays only</SelectItem>
                                    <SelectItem value="weekly">Weekly</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <Button onClick={generateDateRange} className="w-full" size="sm">
                                Generate
                              </Button>
                            </div>
                          </PopoverContent>
                        </Popover>
                      )}

                      {/* Import */}
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" size="sm">
                            <Upload className="h-4 w-4 mr-1" />
                            Import
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-80">
                          <div className="space-y-3">
                            <p className="font-medium text-sm">Import Scenarios</p>
                            <Tabs value={importMode} onValueChange={(v) => setImportMode(v as 'csv' | 'json')}>
                              <TabsList className="grid w-full grid-cols-2 h-8">
                                <TabsTrigger value="csv" className="text-xs">CSV</TabsTrigger>
                                <TabsTrigger value="json" className="text-xs">JSON</TabsTrigger>
                              </TabsList>
                              <TabsContent value="csv" className="mt-2">
                                <Textarea
                                  placeholder={`${scenarioVariables.map(v => v.name).join(',')}\n2025-01-01\n2025-01-02`}
                                  value={importText}
                                  onChange={(e) => setImportText(e.target.value)}
                                  rows={4}
                                  className="font-mono text-xs"
                                />
                              </TabsContent>
                              <TabsContent value="json" className="mt-2">
                                <Textarea
                                  placeholder={`[{"${scenarioVariables[0]?.name || 'key'}": "value"}]`}
                                  value={importText}
                                  onChange={(e) => setImportText(e.target.value)}
                                  rows={4}
                                  className="font-mono text-xs"
                                />
                              </TabsContent>
                            </Tabs>
                            <Button onClick={parseImport} className="w-full" size="sm">
                              Import
                            </Button>
                          </div>
                        </PopoverContent>
                      </Popover>

                      <Button onClick={addScenarioRow} size="sm">
                        <Plus className="h-4 w-4 mr-1" />
                        Add Row
                      </Button>
                    </div>
                  </div>

                  {formData.scenarioRows.length === 0 ? (
                    <Card className="p-6 border-dashed text-center">
                      <p className="text-sm text-muted-foreground">
                        No scenarios yet. Click "Add Row" to add scenarios.
                      </p>
                    </Card>
                  ) : (
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full">
                        <thead className="bg-muted/50">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground w-10">#</th>
                            {scenarioVariables.map(v => (
                              <th key={v.name} className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">
                                <span className="flex items-center gap-1">
                                  {v.name}
                                  {v.required && <span className="text-destructive">*</span>}
                                </span>
                              </th>
                            ))}
                            <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground w-20">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {formData.scenarioRows.map((row, rowIndex) => (
                            <tr key={rowIndex} className="hover:bg-muted/30">
                              <td className="px-3 py-2 text-sm text-muted-foreground">{rowIndex + 1}</td>
                              {scenarioVariables.map(v => {
                                const value = row[v.name] || '';
                                const isMissing = v.required && !value.trim();
                                return (
                                  <td key={v.name} className="px-3 py-2">
                                    {v.name === 'date_override' ? (
                                      <Input
                                        type="date"
                                        value={value}
                                        onChange={(e) => updateScenarioValue(rowIndex, v.name, e.target.value)}
                                        className={cn("h-8", isMissing && "border-destructive")}
                                      />
                                    ) : (
                                      <Input
                                        value={value}
                                        onChange={(e) => updateScenarioValue(rowIndex, v.name, e.target.value)}
                                        placeholder={v.name}
                                        className={cn("h-8", isMissing && "border-destructive")}
                                      />
                                    )}
                                  </td>
                                );
                              })}
                              <td className="px-3 py-2 text-right">
                                <div className="flex justify-end gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0"
                                    onClick={() => cloneScenarioRow(rowIndex)}
                                  >
                                    <Copy className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0"
                                    onClick={() => removeScenarioRow(rowIndex)}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Message when all variables are default */}
              {formData.agent && !loadingVariables && allVariables.length > 0 && scenarioVariables.length === 0 && (
                <Card className="bg-muted/50">
                  <CardContent className="py-4 text-center">
                    <p className="text-sm text-muted-foreground">
                      All variables are set to "Default" mode. This eval will run a single scenario using the default values above.
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      To test multiple scenarios, change one or more variables to "Per Scenario" mode.
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        );

      case 2: // Evaluation Setup (optimization) or Variants (standard)
        if (formData.mode === 'optimization') {
          // Available eval functions for dropdowns
          const availableEvalFunctionOptions = evalFunctions.map(f => ({
            id: f.id,
            name: f.name,
            display_name: f.name,
          }));

          return (
            <div className="space-y-6">
              <div>
                <Label className="text-lg">Evaluation Setup</Label>
                <p className="text-sm text-muted-foreground mt-1">
                  How to evaluate agent outputs and what to optimize
                </p>
              </div>

              <EvaluationSetup
                config={formData.evaluationConfig}
                onChange={(newConfig) => {
                  // Update evaluation config
                  setFormData(prev => ({
                    ...prev,
                    evaluationConfig: newConfig,
                    // Auto-derive objectives from the config
                    objectives: configToObjectives(newConfig),
                    // Also update judge settings for consistency
                    judgeMode: newConfig.qualityMode === 'llm_judge' ? 'llm' :
                               newConfig.qualityMode === 'custom_function' ? 'function' :
                               newConfig.correctnessFunction ? 'function' : 'none',
                    evalFunction: newConfig.correctnessFunction || newConfig.qualityFunctionId || '',
                    passThreshold: newConfig.judgePassThreshold || 0.7,
                    // Sync judge agent from EvaluationSetup
                    judge: newConfig.judgeAgent || prev.judge,
                  }));
                }}
                availableEvalFunctions={availableEvalFunctionOptions}
                availableJudges={judges.map(j => ({ id: j.id, name: j.name, path: j.path }))}
                availableModels={models.map(m => ({ value: m.value, label: m.label }))}
                isOptimization={true}
              />
            </div>
          );
        }

        // Standard mode - Variants
        return (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <Label className="text-lg">A/B Test Variants</Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Configure different model/temperature combinations to compare
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={addVariant}>
                <Plus className="h-4 w-4 mr-1" />
                Add Variant
              </Button>
            </div>

            {formData.variants.map((variant, index) => (
              <Card key={index}>
                <CardContent className="pt-4 space-y-4">
                  {/* Row 1: Name, Model, Delete */}
                  <div className="flex gap-4 items-end">
                    <div className="flex-1 space-y-2">
                      <Label>Name</Label>
                      <Input
                        value={variant.name}
                        onChange={e => updateVariant(index, 'name', e.target.value)}
                      />
                    </div>
                    <div className="flex-1 space-y-2">
                      <Label>Model</Label>
                      <Select
                        value={variant.model}
                        onValueChange={v => updateVariant(index, 'model', v)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                          {models.map(m => (
                            <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeVariant(index)}
                      disabled={formData.variants.length <= 1}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>

                  {/* Row 2: Temperature */}
                  <div className="space-y-2">
                    <Label>Temperature: {variant.temperature}</Label>
                    <Slider
                      value={[variant.temperature]}
                      onValueChange={([v]) => updateVariant(index, 'temperature', v)}
                      min={0}
                      max={1}
                      step={0.1}
                    />
                  </div>

                  {/* Row 3: Agent Version Selector */}
                  {agentVersions.length > 0 && (
                    <div className="space-y-2">
                      <Label>Agent Version</Label>
                      <Select
                        value={variant.agentVersion?.toString() || 'latest'}
                        onValueChange={(value) =>
                          updateVariant(
                            index,
                            'agentVersion',
                            value === 'latest' ? null : parseInt(value, 10)
                          )
                        }
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Latest version" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="latest">Latest version</SelectItem>
                          {agentVersions
                            .filter((v) => !v.is_draft)
                            .map((v) => (
                              <SelectItem
                                key={v.version_number}
                                value={v.version_number.toString()}
                              >
                                v{v.version_number}
                                {v.commit_message && ` - ${v.commit_message}`}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        Compare different agent versions across variants
                      </p>
                    </div>
                  )}

                  {/* Row 4: Graph Settings */}
                  <div className="flex items-center gap-6 pt-2 border-t">
                    <div className="flex items-center gap-2">
                      <Switch
                        id={`parallel-${index}`}
                        checked={variant.graphSettings?.parallelExecution ?? false}
                        onCheckedChange={(checked) => updateVariant(index, 'graphSettings', {
                          ...variant.graphSettings,
                          parallelExecution: checked,
                          maxParallelTasks: variant.graphSettings?.maxParallelTasks ?? 3
                        })}
                      />
                      <Label htmlFor={`parallel-${index}`} className="text-sm font-normal">
                        Parallel Execution
                      </Label>
                    </div>
                    {variant.graphSettings?.parallelExecution && (
                      <div className="flex items-center gap-2">
                        <Label className="text-sm font-normal whitespace-nowrap">Max Parallel Tasks:</Label>
                        <Input
                          type="number"
                          className="w-20"
                          min={1}
                          max={10}
                          value={variant.graphSettings?.maxParallelTasks ?? 3}
                          onChange={e => updateVariant(index, 'graphSettings', {
                            ...variant.graphSettings,
                            parallelExecution: true,
                            maxParallelTasks: parseInt(e.target.value) || 3
                          })}
                        />
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        );

      case 3: // Judge (standard) or Advanced (optimization)
        // For optimization mode, this is the Advanced step (step 3 is the last step)
        if (formData.mode === 'optimization') {
          const opt = formData.optimization;
          return (
            <div className="space-y-6">
              {/* Goal & Directives Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Optimization Goal & Directives</CardTitle>
                  <CardDescription>
                    Guide the optimizer with a goal description and standing directives
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Goal Description</Label>
                    <Textarea
                      placeholder="Describe what you want to optimize for, e.g., 'Improve accuracy on financial predictions while maintaining response time under 5 seconds'"
                      value={opt.goalDescription}
                      onChange={e => setFormData(prev => ({
                        ...prev,
                        optimization: { ...prev.optimization, goalDescription: e.target.value }
                      }))}
                      rows={3}
                    />
                    <p className="text-xs text-muted-foreground">
                      A clear description helps the optimizer understand your priorities
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label>Standing Directives</Label>
                    <div className="space-y-2">
                      {opt.directives.map((directive, index) => (
                        <div key={index} className="flex gap-2">
                          <Input
                            value={directive}
                            onChange={e => {
                              const newDirectives = [...opt.directives];
                              newDirectives[index] = e.target.value;
                              setFormData(prev => ({
                                ...prev,
                                optimization: { ...prev.optimization, directives: newDirectives }
                              }));
                            }}
                            placeholder={`Directive ${index + 1}`}
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              const newDirectives = opt.directives.filter((_, i) => i !== index);
                              setFormData(prev => ({
                                ...prev,
                                optimization: { ...prev.optimization, directives: newDirectives }
                              }));
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            optimization: { ...prev.optimization, directives: [...opt.directives, ''] }
                          }));
                        }}
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Add Directive
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Rules the optimizer must always follow, e.g., "Never remove safety disclaimers"
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Optimizer Settings Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Optimizer Settings</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Max Iterations</Label>
                      <Input
                        type="number"
                        min={1}
                        max={100}
                        value={opt.maxIterations}
                        onChange={e => setFormData(prev => ({
                          ...prev,
                          optimization: { ...prev.optimization, maxIterations: parseInt(e.target.value) || 10 }
                        }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Trials per Iteration</Label>
                      <Input
                        type="number"
                        min={1}
                        max={10}
                        value={opt.trialsPerIteration}
                        onChange={e => setFormData(prev => ({
                          ...prev,
                          optimization: { ...prev.optimization, trialsPerIteration: parseInt(e.target.value) || 3 }
                        }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Checkpoint Interval</Label>
                      <Input
                        type="number"
                        min={1}
                        value={opt.checkpointInterval}
                        onChange={e => setFormData(prev => ({
                          ...prev,
                          optimization: { ...prev.optimization, checkpointInterval: parseInt(e.target.value) || 5 }
                        }))}
                      />
                      <p className="text-xs text-muted-foreground">Pause for review every N iterations</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Checkpoint Timeout (hours)</Label>
                      <Input
                        type="number"
                        min={1}
                        value={opt.checkpointTimeoutHours}
                        onChange={e => setFormData(prev => ({
                          ...prev,
                          optimization: { ...prev.optimization, checkpointTimeoutHours: parseInt(e.target.value) || 24 }
                        }))}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          );
        }

        // Standard mode - Judge step
        return (
          <div className="space-y-6">
            {/* Judge Mode Selector */}
            <div className="space-y-4">
              <div>
                <Label className="text-base font-medium">Evaluation Method</Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Choose how agent outputs will be evaluated
                </p>
              </div>

              <RadioGroup
                value={formData.judgeMode}
                onValueChange={(value: 'none' | 'llm' | 'function' | 'both') => {
                  setFormData(prev => {
                    const updates: Record<string, unknown> = { judgeMode: value };
                    // Clear selections when switching modes
                    if (value === 'none') {
                      updates.judge = '';
                      updates.evalFunction = '';
                      updates.evalFunctionConfig = {};
                      // For optimization mode, default to metric-based objective
                      if (prev.mode === 'optimization') {
                        updates.objectives = [
                          { name: 'latency', source: 'metric', source_key: 'latency_ms', direction: 'minimize', weight: 1.0 }
                        ];
                      }
                    } else if (value === 'llm') {
                      updates.evalFunction = '';
                      updates.evalFunctionConfig = {};
                      // For optimization mode, set judge_score objective
                      if (prev.mode === 'optimization') {
                        updates.objectives = [
                          { name: 'quality', source: 'judge_score', direction: 'maximize', weight: 1.0 }
                        ];
                      }
                    } else if (value === 'function') {
                      updates.judge = '';
                      // For optimization mode, set eval_function objective
                      if (prev.mode === 'optimization') {
                        updates.objectives = [
                          { name: 'accuracy', source: 'eval_function', direction: 'maximize', weight: 0, required: true, target_threshold: 1.0 }
                        ];
                      }
                    } else if (value === 'both') {
                      // For optimization mode, use both judge and eval function
                      if (prev.mode === 'optimization') {
                        updates.objectives = [
                          { name: 'accuracy', source: 'eval_function', direction: 'maximize', weight: 0, required: true, target_threshold: 1.0 },
                          { name: 'quality', source: 'judge_score', direction: 'maximize', weight: 1.0 }
                        ];
                      }
                    }
                    return { ...prev, ...updates };
                  });
                }}
                className="grid grid-cols-2 gap-3"
              >
                <div className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-muted/50 cursor-pointer">
                  <RadioGroupItem value="none" id="judge-none" className="mt-1" />
                  <div className="space-y-1">
                    <Label htmlFor="judge-none" className="font-medium cursor-pointer">No Evaluation</Label>
                    <p className="text-xs text-muted-foreground">
                      Run agent only, no scoring
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-muted/50 cursor-pointer">
                  <RadioGroupItem value="llm" id="judge-llm" className="mt-1" />
                  <div className="space-y-1">
                    <Label htmlFor="judge-llm" className="font-medium cursor-pointer">LLM Judge Only</Label>
                    <p className="text-xs text-muted-foreground">
                      AI evaluates output quality
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-muted/50 cursor-pointer">
                  <RadioGroupItem value="function" id="judge-function" className="mt-1" />
                  <div className="space-y-1">
                    <Label htmlFor="judge-function" className="font-medium cursor-pointer">Function Only</Label>
                    <p className="text-xs text-muted-foreground">
                      Custom code determines pass/fail
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-muted/50 cursor-pointer">
                  <RadioGroupItem value="both" id="judge-both" className="mt-1" />
                  <div className="space-y-1">
                    <Label htmlFor="judge-both" className="font-medium cursor-pointer">Function + LLM Judge</Label>
                    <p className="text-xs text-muted-foreground">
                      Function provides ground truth, LLM evaluates
                    </p>
                  </div>
                </div>
              </RadioGroup>
            </div>

            {/* LLM Judge Selection - shown for 'llm' and 'both' modes */}
            {(formData.judgeMode === 'llm' || formData.judgeMode === 'both') && (
              <>
                <Separator />
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Sparkles className="h-4 w-4" />
                      LLM Judge Configuration
                    </CardTitle>
                    <CardDescription className="text-xs">
                      {formData.judgeMode === 'both'
                        ? 'AI will evaluate output using ground truth from the eval function'
                        : 'AI will evaluate output quality and provide a score'}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label>Select Judge</Label>
                      <Popover open={judgePopoverOpen} onOpenChange={setJudgePopoverOpen} modal={true}>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="w-full justify-start">
                            {formData.judge
                              ? judges.find(j => j.path === formData.judge)?.name || formData.judge
                              : 'Select a judge...'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[400px] p-0">
                          <Command
                            filter={(value, search) => {
                              if (!search) return 1;
                              const searchLower = search.toLowerCase();
                              return value.toLowerCase().includes(searchLower) ? 1 : 0;
                            }}
                          >
                            <CommandInput placeholder="Search judges..." />
                            <CommandList>
                              <CommandEmpty>No judges found.</CommandEmpty>
                              <CommandGroup>
                                {judges.map(judge => (
                                  <CommandItem
                                    key={judge.id}
                                    value={`${judge.name} ${judge.path}`}
                                    onSelect={() => {
                                      setFormData(prev => ({ ...prev, judge: judge.path }));
                                      setJudgePopoverOpen(false);
                                    }}
                                  >
                                    <div>
                                      <p className="font-medium">{judge.name}</p>
                                      <p className="text-xs text-muted-foreground">{judge.path}</p>
                                    </div>
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </div>

                    {formData.judge && (
                      <>
                        <div className="space-y-2">
                          <Label>Pass Threshold: {formData.passThreshold}</Label>
                          <Slider
                            value={[formData.passThreshold]}
                            onValueChange={([v]) => setFormData(prev => ({ ...prev, passThreshold: v }))}
                            min={0}
                            max={1}
                            step={0.05}
                          />
                          <p className="text-xs text-muted-foreground">
                            Scores above this threshold are considered passing
                          </p>
                        </div>

                      </>
                    )}
                  </CardContent>
                </Card>
              </>
            )}

            {/* Eval Function Selection - shown for 'function' and 'both' modes */}
            {(formData.judgeMode === 'function' || formData.judgeMode === 'both') && (
              <>
                <Separator />
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Beaker className="h-4 w-4" />
                      Eval Function Configuration
                    </CardTitle>
                    <CardDescription className="text-xs">
                      {formData.judgeMode === 'both'
                        ? 'Function provides ground truth data for the LLM judge to use'
                        : 'Custom function determines pass/fail and score'}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label>Select Function</Label>
                      <Popover open={functionPopoverOpen} onOpenChange={setFunctionPopoverOpen} modal={true}>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="w-full justify-start">
                            {formData.evalFunction
                              ? evalFunctions.find(f => f.id === formData.evalFunction)?.name
                              : 'Select a function...'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[400px] p-0">
                          <Command
                            filter={(value, search) => {
                              if (!search) return 1;
                              const searchLower = search.toLowerCase();
                              return value.toLowerCase().includes(searchLower) ? 1 : 0;
                            }}
                          >
                            <CommandInput placeholder="Search functions..." />
                            <CommandList>
                              <CommandEmpty>No functions found.</CommandEmpty>
                              <CommandGroup>
                                {evalFunctions.map(func => (
                                  <CommandItem
                                    key={func.id}
                                    value={`${func.name} ${func.description || ''}`}
                                    onSelect={() => {
                                      // Initialize config with default values from schema
                                      const defaultConfig: Record<string, unknown> = {};
                                      if (func.config_schema?.properties) {
                                        Object.entries(func.config_schema.properties).forEach(([key, prop]) => {
                                          if (prop.default !== undefined) {
                                            defaultConfig[key] = prop.default;
                                          } else {
                                            // Set type-appropriate defaults
                                            switch (prop.type) {
                                              case 'string': defaultConfig[key] = ''; break;
                                              case 'number':
                                              case 'integer': defaultConfig[key] = 0; break;
                                              case 'boolean': defaultConfig[key] = false; break;
                                              default: defaultConfig[key] = '';
                                            }
                                          }
                                        });
                                      }
                                      setFormData(prev => {
                                        const updates: Record<string, unknown> = {
                                          evalFunction: func.id,
                                          evalFunctionConfig: defaultConfig as Record<string, string>
                                        };
                                        // Auto-update objectives when eval function is selected in optimization mode
                                        if (prev.mode === 'optimization') {
                                          if (prev.judgeMode === 'function') {
                                            // Function only mode - use eval_function as required
                                            updates.objectives = [
                                              { name: 'accuracy', source: 'eval_function', source_key: func.id, direction: 'maximize', weight: 0, required: true, target_threshold: 1.0 }
                                            ];
                                          } else if (prev.judgeMode === 'both') {
                                            // Both mode - eval_function required + judge_score weighted
                                            updates.objectives = [
                                              { name: 'accuracy', source: 'eval_function', source_key: func.id, direction: 'maximize', weight: 0, required: true, target_threshold: 1.0 },
                                              { name: 'quality', source: 'judge_score', direction: 'maximize', weight: 1.0 }
                                            ];
                                          }
                                        }
                                        return { ...prev, ...updates };
                                      });
                                      setFunctionPopoverOpen(false);
                                    }}
                                  >
                                    <div>
                                      <div className="flex items-center gap-2">
                                        <p className="font-medium">{func.name}</p>
                                        {func.returns_ground_truth && (
                                          <Badge variant="outline" className="text-green-600 text-xs">
                                            Ground Truth
                                          </Badge>
                                        )}
                                      </div>
                                      {func.description && (
                                        <p className="text-xs text-muted-foreground">{func.description}</p>
                                      )}
                                    </div>
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </div>

                    {/* Function Config Inputs - Schema-driven */}
                    {formData.evalFunction && (() => {
                      const selectedFunc = evalFunctions.find(f => f.id === formData.evalFunction);
                      const schema = selectedFunc?.config_schema;
                      const properties = schema?.properties || {};
                      const required = schema?.required || [];
                      const hasParams = Object.keys(properties).length > 0;

                      if (!hasParams) {
                        return (
                          <div className="bg-muted/50 rounded-md p-3">
                            <p className="text-xs text-muted-foreground">
                              This function has no configurable parameters.
                            </p>
                          </div>
                        );
                      }

                      return (
                        <div className="bg-muted/50 rounded-md p-3 space-y-4">
                          <div className="flex items-center gap-2">
                            <Label className="text-xs font-medium text-muted-foreground">Function Parameters</Label>
                          </div>

                          <div className="space-y-3">
                            {Object.entries(properties).map(([paramName, paramDef]) => {
                              const isRequired = required.includes(paramName);
                              const value = formData.evalFunctionConfig[paramName] ?? '';

                              return (
                                <div key={paramName} className="space-y-1">
                                  <div className="flex items-center gap-2">
                                    <Label htmlFor={`param-${paramName}`} className="text-sm">
                                      {paramName}
                                      {isRequired && <span className="text-destructive ml-1">*</span>}
                                    </Label>
                                    <Badge variant="outline" className="text-xs">
                                      {paramDef.type}
                                    </Badge>
                                  </div>

                                  {/* Render input based on type */}
                                  {paramDef.type === 'boolean' ? (
                                    <div className="flex items-center gap-2">
                                      <Switch
                                        id={`param-${paramName}`}
                                        checked={Boolean(value)}
                                        onCheckedChange={(checked) => {
                                          setFormData((prev) => ({
                                            ...prev,
                                            evalFunctionConfig: {
                                              ...prev.evalFunctionConfig,
                                              [paramName]: checked,
                                            } as Record<string, string>,
                                          }));
                                        }}
                                      />
                                      <span className="text-sm text-muted-foreground">
                                        {value ? 'Yes' : 'No'}
                                      </span>
                                    </div>
                                  ) : paramDef.enum ? (
                                    <Select
                                      value={String(value)}
                                      onValueChange={(v) => {
                                        setFormData((prev) => ({
                                          ...prev,
                                          evalFunctionConfig: {
                                            ...prev.evalFunctionConfig,
                                            [paramName]: v,
                                          } as Record<string, string>,
                                        }));
                                      }}
                                    >
                                      <SelectTrigger id={`param-${paramName}`}>
                                        <SelectValue placeholder={`Select ${paramName}`} />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {paramDef.enum.map((enumVal) => (
                                          <SelectItem key={String(enumVal)} value={String(enumVal)}>
                                            {String(enumVal)}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  ) : (
                                    <Input
                                      id={`param-${paramName}`}
                                      type={paramDef.type === 'number' || paramDef.type === 'integer' ? 'number' : 'text'}
                                      min={paramDef.minimum}
                                      max={paramDef.maximum}
                                      placeholder={paramDef.default !== undefined ? `Default: ${paramDef.default}` : `Enter ${paramName}`}
                                      value={String(value)}
                                      onChange={(e) => {
                                        let newValue: string | number = e.target.value;
                                        if (paramDef.type === 'number') {
                                          newValue = parseFloat(e.target.value) || 0;
                                        } else if (paramDef.type === 'integer') {
                                          newValue = parseInt(e.target.value) || 0;
                                        }
                                        setFormData((prev) => ({
                                          ...prev,
                                          evalFunctionConfig: {
                                            ...prev.evalFunctionConfig,
                                            [paramName]: newValue,
                                          } as Record<string, string>,
                                        }));
                                      }}
                                    />
                                  )}

                                  {paramDef.description && (
                                    <p className="text-xs text-muted-foreground">
                                      {paramDef.description}
                                    </p>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })()}
                  </CardContent>
                </Card>
              </>
            )}

            {/* Info box for "none" mode */}
            {formData.judgeMode === 'none' && (
              <Alert>
                <AlertDescription className="text-sm">
                  Without evaluation, runs will complete but won't have pass/fail status or scores.
                  This is useful for benchmarking agent behavior without judging correctness.
                </AlertDescription>
              </Alert>
            )}
          </div>
        );

      case 4: // Advanced
        if (formData.mode === 'optimization') {
          const opt = formData.optimization;
          return (
            <div className="space-y-6">
              {/* Goal & Directives Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Optimization Goal & Directives</CardTitle>
                  <CardDescription>
                    Guide the optimizer with a goal description and standing directives
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Goal Description</Label>
                    <Textarea
                      placeholder="Describe what you want to optimize for, e.g., 'Improve accuracy on financial predictions while maintaining response time under 5 seconds'"
                      value={opt.goalDescription}
                      onChange={e => setFormData(prev => ({
                        ...prev,
                        optimization: { ...prev.optimization, goalDescription: e.target.value }
                      }))}
                      rows={3}
                    />
                    <p className="text-xs text-muted-foreground">
                      Natural language description of your optimization goal
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label>Directives</Label>
                    <p className="text-xs text-muted-foreground mb-2">
                      Standing rules the optimizer must follow (e.g., "Keep prompt under 500 words", "Don't remove any tools")
                    </p>
                    <div className="space-y-2">
                      {opt.directives.map((directive, idx) => (
                        <div key={idx} className="flex gap-2">
                          <Input
                            value={directive}
                            onChange={e => {
                              const newDirectives = [...opt.directives];
                              newDirectives[idx] = e.target.value;
                              setFormData(prev => ({
                                ...prev,
                                optimization: { ...prev.optimization, directives: newDirectives }
                              }));
                            }}
                            placeholder="Enter a directive..."
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              const newDirectives = opt.directives.filter((_, i) => i !== idx);
                              setFormData(prev => ({
                                ...prev,
                                optimization: { ...prev.optimization, directives: newDirectives }
                              }));
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setFormData(prev => ({
                          ...prev,
                          optimization: { ...prev.optimization, directives: [...prev.optimization.directives, ''] }
                        }))}
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Add Directive
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Resource Limits</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Max Iterations</Label>
                    <Input
                      type="number"
                      value={opt.maxIterations}
                      onChange={e => setFormData(prev => ({
                        ...prev,
                        optimization: { ...prev.optimization, maxIterations: parseInt(e.target.value) || 10 }
                      }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Max Eval Runs</Label>
                    <Input
                      type="number"
                      value={opt.maxEvalRuns}
                      onChange={e => setFormData(prev => ({
                        ...prev,
                        optimization: { ...prev.optimization, maxEvalRuns: parseInt(e.target.value) || 50 }
                      }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Max Duration (hours)</Label>
                    <Input
                      type="number"
                      value={opt.maxDurationHours}
                      onChange={e => setFormData(prev => ({
                        ...prev,
                        optimization: { ...prev.optimization, maxDurationHours: parseFloat(e.target.value) || 4 }
                      }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Trials Per Iteration</Label>
                    <Input
                      type="number"
                      value={opt.trialsPerIteration}
                      onChange={e => setFormData(prev => ({
                        ...prev,
                        optimization: { ...prev.optimization, trialsPerIteration: parseInt(e.target.value) || 3 }
                      }))}
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Exploration Strategy</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Initial Mode</Label>
                    <Select
                      value={opt.initialMode}
                      onValueChange={v => setFormData(prev => ({
                        ...prev,
                        optimization: { ...prev.optimization, initialMode: v as 'sequential' | 'parallel' }
                      }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sequential">Sequential</SelectItem>
                        <SelectItem value="parallel">Parallel (Beam Search)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Beam Width</Label>
                      <Input
                        type="number"
                        value={opt.beamWidth}
                        onChange={e => setFormData(prev => ({
                          ...prev,
                          optimization: { ...prev.optimization, beamWidth: parseInt(e.target.value) || 3 }
                        }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Plateau Window</Label>
                      <Input
                        type="number"
                        value={opt.plateauWindow}
                        onChange={e => setFormData(prev => ({
                          ...prev,
                          optimization: { ...prev.optimization, plateauWindow: parseInt(e.target.value) || 3 }
                        }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Plateau Threshold</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={opt.plateauThreshold}
                        onChange={e => setFormData(prev => ({
                          ...prev,
                          optimization: { ...prev.optimization, plateauThreshold: parseFloat(e.target.value) || 0.01 }
                        }))}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Human Review</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Checkpoint Every N Iterations</Label>
                    <Input
                      type="number"
                      value={opt.checkpointInterval}
                      onChange={e => setFormData(prev => ({
                        ...prev,
                        optimization: { ...prev.optimization, checkpointInterval: parseInt(e.target.value) || 5 }
                      }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Checkpoint Timeout (hours)</Label>
                    <Input
                      type="number"
                      value={opt.checkpointTimeoutHours}
                      onChange={e => setFormData(prev => ({
                        ...prev,
                        optimization: { ...prev.optimization, checkpointTimeoutHours: parseInt(e.target.value) || 24 }
                      }))}
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Optimizer Model</CardTitle>
                </CardHeader>
                <CardContent>
                  <Select
                    value={opt.optimizerModel}
                    onValueChange={v => setFormData(prev => ({
                      ...prev,
                      optimization: { ...prev.optimization, optimizerModel: v }
                    }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {models.map(m => (
                        <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </CardContent>
              </Card>
            </div>
          );
        }

        // Standard mode advanced
        return (
          <div className="space-y-6">
            <div className="space-y-4">
              <Label>Metrics to Collect</Label>
              <div className="flex gap-2 flex-wrap">
                {['latency_ms', 'total_tokens', 'tool_call_count', 'tool_success_rate'].map(metric => (
                  <Badge
                    key={metric}
                    variant={formData.metrics.includes(metric) ? 'default' : 'outline'}
                    className="cursor-pointer"
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      metrics: prev.metrics.includes(metric)
                        ? prev.metrics.filter(m => m !== metric)
                        : [...prev.metrics, metric]
                    }))}
                  >
                    {metric.replace('_', ' ')}
                  </Badge>
                ))}
              </div>
            </div>

            {/* HITL Strategy Configuration - only show if agent uses HITL */}
            {agentUsesHITL && (
              <>
                <Separator />
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      HITL Strategy
                      <Badge variant="outline" className="text-yellow-600 border-yellow-500">
                        Agent uses HITL
                      </Badge>
                    </CardTitle>
                    <CardDescription>
                      This agent uses Human-in-the-Loop decision points. Configure how they should be responded to during evaluation runs.
                      {agentHITL?.decision_points && agentHITL.decision_points.length > 0 && (
                        <span className="block mt-1 text-xs">
                          Decision points: {agentHITL.decision_points.join(', ')}
                        </span>
                      )}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Strategy</Label>
                  <Select
                    value={formData.hitl.strategy}
                    onValueChange={v => setFormData(prev => ({
                      ...prev,
                      hitl: { ...prev.hitl, strategy: v as HITLConfig['strategy'] }
                    }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bypass">
                        <div className="flex flex-col">
                          <span>Bypass (auto-approve all)</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="mock">
                        <div className="flex flex-col">
                          <span>Mock Responses</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="llm_responder">
                        <div className="flex flex-col">
                          <span>LLM-as-Human Responder</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {formData.hitl.strategy === 'bypass' && 'Automatically approve all HITL requests - useful for testing agent flow without blocking.'}
                    {formData.hitl.strategy === 'mock' && 'Define specific responses for each decision point - deterministic and repeatable.'}
                    {formData.hitl.strategy === 'llm_responder' && 'Use an LLM with a persona to generate responses - realistic but non-deterministic.'}
                  </p>
                </div>

                {/* Bypass Response Config */}
                {formData.hitl.strategy === 'bypass' && (
                  <div className="space-y-2 pt-2">
                    <Label>Default Action</Label>
                    <Select
                      value={(formData.hitl.bypassResponse?.action as string) || 'approve'}
                      onValueChange={v => setFormData(prev => ({
                        ...prev,
                        hitl: { ...prev.hitl, bypassResponse: { action: v } }
                      }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="approve">Approve</SelectItem>
                        <SelectItem value="reject">Reject</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Mock Responses Config */}
                {formData.hitl.strategy === 'mock' && (
                  <div className="space-y-4 pt-2">
                    <div className="flex justify-between items-center">
                      <Label>Mock Responses</Label>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setFormData(prev => ({
                          ...prev,
                          hitl: {
                            ...prev.hitl,
                            mockResponses: [...prev.hitl.mockResponses, { decisionPointId: '', response: { action: 'approve' } }]
                          }
                        }))}
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Add Response
                      </Button>
                    </div>

                    {formData.hitl.mockResponses.length === 0 && (
                      <Card className="p-4 border-dashed">
                        <p className="text-sm text-muted-foreground text-center">
                          No mock responses defined. Add responses for each decision point.
                        </p>
                      </Card>
                    )}

                    {formData.hitl.mockResponses.map((mockResp, index) => (
                      <Card key={index} className="p-3">
                        <div className="flex items-start gap-2">
                          <div className="flex-1 space-y-3">
                            <div className="space-y-1">
                              <Label className="text-xs">Decision Point ID</Label>
                              <Input
                                placeholder="e.g., review_content, approve_trade"
                                value={mockResp.decisionPointId}
                                onChange={e => setFormData(prev => ({
                                  ...prev,
                                  hitl: {
                                    ...prev.hitl,
                                    mockResponses: prev.hitl.mockResponses.map((r, i) =>
                                      i === index ? { ...r, decisionPointId: e.target.value } : r
                                    )
                                  }
                                }))}
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Response (JSON)</Label>
                              <Textarea
                                placeholder='{"action": "approve"}'
                                value={JSON.stringify(mockResp.response, null, 2)}
                                rows={2}
                                onChange={e => {
                                  try {
                                    const parsed = JSON.parse(e.target.value);
                                    setFormData(prev => ({
                                      ...prev,
                                      hitl: {
                                        ...prev.hitl,
                                        mockResponses: prev.hitl.mockResponses.map((r, i) =>
                                          i === index ? { ...r, response: parsed } : r
                                        )
                                      }
                                    }));
                                  } catch {
                                    // Invalid JSON, ignore
                                  }
                                }}
                              />
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setFormData(prev => ({
                              ...prev,
                              hitl: {
                                ...prev.hitl,
                                mockResponses: prev.hitl.mockResponses.filter((_, i) => i !== index)
                              }
                            }))}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}

                {/* LLM Responder Config */}
                {formData.hitl.strategy === 'llm_responder' && (
                  <div className="space-y-4 pt-2">
                    <div className="space-y-2">
                      <Label>LLM Model</Label>
                      <Select
                        value={formData.hitl.llmModel}
                        onValueChange={v => setFormData(prev => ({
                          ...prev,
                          hitl: { ...prev.hitl, llmModel: v }
                        }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {models.map(m => (
                            <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        A smaller/faster model is recommended for cost efficiency
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label>Persona</Label>
                      <Input
                        value={formData.hitl.llmPersona}
                        onChange={e => setFormData(prev => ({
                          ...prev,
                          hitl: { ...prev.hitl, llmPersona: e.target.value }
                        }))}
                        placeholder="e.g., helpful_reviewer, strict_approver"
                      />
                      <p className="text-xs text-muted-foreground">
                        Defines the LLM's behavior when reviewing HITL requests
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label>Temperature: {formData.hitl.llmTemperature}</Label>
                      <Slider
                        value={[formData.hitl.llmTemperature]}
                        onValueChange={([v]) => setFormData(prev => ({
                          ...prev,
                          hitl: { ...prev.hitl, llmTemperature: v }
                        }))}
                        min={0}
                        max={1}
                        step={0.1}
                      />
                      <p className="text-xs text-muted-foreground">
                        Lower for more consistent responses, higher for more variation
                      </p>
                    </div>
                  </div>
                )}
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={isOpen => !isOpen && handleClose()}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {startOptimizationMode ? 'Start Optimization' : editMode ? 'Edit Evaluation' : 'Create Evaluation'}
          </DialogTitle>
        </DialogHeader>

        {/* Step indicator */}
        <div className="flex items-center justify-between mb-6">
          {WIZARD_STEPS.map((step, index) => (
            <div
              key={step.id}
              className={cn(
                "flex items-center",
                index < WIZARD_STEPS.length - 1 && "flex-1"
              )}
            >
              <div className="flex flex-col items-center">
                <div
                  className={cn(
                    "w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium border-2 transition-colors",
                    index < currentStep && "bg-primary border-primary text-primary-foreground",
                    index === currentStep && "border-primary text-primary",
                    index > currentStep && "border-muted text-muted-foreground"
                  )}
                >
                  {index + 1}
                </div>
                <span className="text-xs mt-1 text-muted-foreground">{step.label}</span>
              </div>
              {index < WIZARD_STEPS.length - 1 && (
                <div
                  className={cn(
                    "flex-1 h-0.5 mx-2",
                    index < currentStep ? "bg-primary" : "bg-muted"
                  )}
                />
              )}
            </div>
          ))}
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="min-h-[400px]">
          {renderStep()}
        </div>

        <DialogFooter className="flex justify-between mt-6">
          <Button variant="outline" onClick={goBack} disabled={currentStep === 0}>
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            {currentStep < WIZARD_STEPS.length - 1 ? (
              <Button onClick={goNext} disabled={!canGoNext()}>
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            ) : (
              <Button onClick={handleSubmit} disabled={loading || !canGoNext()}>
                {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {startOptimizationMode ? 'Start Optimization' : editMode ? 'Save Changes' : 'Create Evaluation'}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default EvalWizard;
